import { Button } from '@edsn/pie-components';
import IconDelete from '@iconify/icons-material-symbols/delete-outline';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '#pie/components/icon/Icon';
import { Dialog } from '#pie/components/layout/dialog/Dialog';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { useToast } from '#pie/components/toast/ToastContext';
import type { ContactResponse } from '#pie/internal/api/pie-bff';
import {
  getGetTeamIdQueryKey,
  useRemoveContactFromTeam,
} from '#pie/internal/api/pie-bff';

interface RemoveTeamMemberButtonProps {
  data: ContactResponse;
  teamId: string;
}

export const RemoveTeamMemberButton = ({
  data,
  teamId,
}: RemoveTeamMemberButtonProps) => {
  const { addToast } = useToast();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const { t } = useTranslation();
  const { t: tom } = useTranslation('organisationManagement');
  const queryClient = useQueryClient();

  const { mutate, isPending } = useRemoveContactFromTeam({
    mutation: {
      onError: (error) => {
        addToast({
          message: `${tom('error.message.delete_user')}`,
          title: `${t('common.errors.delete_user')}`,
          type: 'error',
          error: error,
        });
      },
      onSuccess: () => {
        addToast({
          message: `${t('common.success.message.delete', { name: data.fullName })}`,
          title: `${t('common.deleted_user')}`,
          type: 'success',
        });
        setOpenDeleteDialog(false);
        queryClient.invalidateQueries({
          predicate: (m) => {
            return m.queryKey.some(
              (n) => typeof n === 'string' && n.includes(data.id),
            );
          },
        });
        queryClient.invalidateQueries({
          queryKey: getGetTeamIdQueryKey(teamId),
        });
        queryClient.removeQueries({
          queryKey: ['/contact/details'],
        });
      },
    },
  });

  return (
    <>
      <Button
        iconStart={<Icon icon={IconDelete} />}
        action="tertiary"
        emphasis="minimal"
        onClick={() => setOpenDeleteDialog(true)}
        className="ml-auto p-0"
        aria-label={tom('delete.title.delete_user')}
      />
      <Dialog
        title={tom('delete.title.delete_user')}
        open={openDeleteDialog}
        onOpenChange={setOpenDeleteDialog}
      >
        <Stack gap="lg">
          <Text>
            {tom('delete.message.delete_user', { name: data.fullName })}
          </Text>
          <div className="grid-auto grid auto-cols-fr grid-flow-col gap-2">
            <Button
              action="tertiary"
              emphasis="subtle"
              onClick={() => {
                setOpenDeleteDialog(false);
              }}
            >
              {t('common.button.cancel.delete_user')}
            </Button>
            <Button
              isLoading={isPending}
              action="danger"
              emphasis="bold"
              onClick={() => mutate({ contactId: data.id, teamId: teamId })}
            >
              {t('common.delete_user')}
            </Button>
          </div>
        </Stack>
      </Dialog>
    </>
  );
};
